/**
 * DispoPhotoCaptureModal — Jul-2026 (User-Wunsch).
 *
 * „Foto"-Button an jeder Dispo-Fahrzeugkarte: geführte Fahrzeugfotos mit
 * vorgegebenen Positionen (TACHO, VORNE, LINKS, …) direkt mit der Kamera
 * aufnehmen — jedes Foto wird sofort am Fahrzeug (Inserat) gespeichert.
 * Danach optional ein Video aufnehmen/hochladen (listing.video_url).
 */
import { useRef, useState } from "react";
import { Camera, CheckCircle2, Loader2, Video } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const PHOTO_SLOTS = ["TACHO", "VORNE", "LINKS", "RECHTS", "HINTEN", "OBEN", "SCHADEN 1", "SCHADEN 2"];
const abs = (u) => (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

export default function DispoPhotoCaptureModal({ item, open, onOpenChange }) {
    const [done, setDone] = useState({});          // slot -> url
    const [busySlot, setBusySlot] = useState(null);
    const [videoBusy, setVideoBusy] = useState(false);
    const [videoDone, setVideoDone] = useState(false);
    const fileRef = useRef(null);
    const videoRef = useRef(null);
    const pendingSlot = useRef(null);

    const pick = (slot) => { pendingSlot.current = slot; fileRef.current?.click(); };

    const onPhoto = async (file) => {
        const slot = pendingSlot.current;
        if (!file || !slot) return;
        if (file.size > 15 * 1024 * 1024) { toast.error("Foto zu groß (max. 15 MB)"); return; }
        setBusySlot(slot);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const url = abs(data.url);
            await api.post(`/listings/${item.id}/dispo/media`, { images: [{ url, label: slot }] });
            setDone((p) => ({ ...p, [slot]: url }));
            toast.success(`Foto „${slot}" gespeichert`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Foto-Upload fehlgeschlagen");
        } finally {
            setBusySlot(null);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const onVideo = async (file) => {
        if (!file) return;
        if (file.size > 200 * 1024 * 1024) { toast.error("Video zu groß (max. 200 MB)"); return; }
        setVideoBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.post(`/listings/${item.id}/dispo/media`, { video_url: abs(data.url) });
            setVideoDone(true);
            toast.success("Video am Fahrzeug gespeichert");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Video-Upload fehlgeschlagen");
        } finally {
            setVideoBusy(false);
            if (videoRef.current) videoRef.current.value = "";
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="dispo-photo-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-base">
                        <Camera className="w-4 h-4" /> Fotos aufnehmen — {item.title || "Fahrzeug"}
                    </DialogTitle>
                </DialogHeader>
                <p className="text-[11px] text-gray-500 -mt-1">
                    Jede Position antippen → Kamera öffnet sich → Foto wird sofort am Fahrzeug gespeichert.
                </p>
                <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={(e) => onPhoto(e.target.files?.[0])} data-testid="dispo-photo-input" />
                <input ref={videoRef} type="file" accept="video/*" capture="environment" className="hidden"
                    onChange={(e) => onVideo(e.target.files?.[0])} data-testid="dispo-video-input" />
                <div className="grid grid-cols-2 gap-2">
                    {PHOTO_SLOTS.map((slot) => (
                        <button key={slot} type="button" onClick={() => pick(slot)} disabled={busySlot !== null}
                            className={`h-12 rounded-sm border text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-colors ${done[slot] ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-gray-300 bg-white hover:bg-gray-50 text-gray-700"}`}
                            data-testid={`dispo-photo-slot-${slot.replace(/\s/g, "-")}-${item.id}`}>
                            {busySlot === slot ? <Loader2 className="w-4 h-4 animate-spin" />
                                : done[slot] ? <CheckCircle2 className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
                            {slot}
                        </button>
                    ))}
                </div>
                <div className="border-t border-gray-100 pt-3 mt-1">
                    <button type="button" onClick={() => videoRef.current?.click()} disabled={videoBusy}
                        className={`w-full h-11 rounded-sm text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 ${videoDone ? "bg-emerald-50 border border-emerald-400 text-emerald-700" : "bg-[#111827] text-[#FFCC00] hover:bg-black"}`}
                        data-testid={`dispo-video-btn-${item.id}`}>
                        {videoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : videoDone ? <CheckCircle2 className="w-4 h-4" /> : <Video className="w-4 h-4" />}
                        {videoDone ? "Video gespeichert — neues aufnehmen" : "Video aufnehmen / hochladen"}
                    </button>
                    <p className="text-[10px] text-gray-400 mt-1">Optional: nach den Fotos ein Rundgang-Video (max. 200 MB).</p>
                </div>
                <div className="text-[10px] text-gray-400">
                    {Object.keys(done).length} von {PHOTO_SLOTS.length} Positionen in dieser Sitzung aufgenommen · vorhandene Bilder am Fahrzeug: {(item.images || []).length}
                </div>
            </DialogContent>
        </Dialog>
    );
}
